import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Activity, 
  Database, 
  Shield, 
  Cloud, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Download
} from "lucide-react";

interface HealthCheck {
  timestamp: string;
  status: 'healthy' | 'warning' | 'error';
  checks: {
    database?: { status: string; response_time_ms?: number; error?: string };
    auth?: { status: string; error?: string };
    storage?: { status: string; buckets_count?: number; error?: string };
    performance?: { status: string; query_time_ms?: number; records_tested?: number; error?: string };
    metrics?: { status: string; total_users?: number; active_sessions?: number; recent_errors?: number };
  };
  alerts: string[];
}

export const SystemHealthMonitor = () => {
  const [healthData, setHealthData] = useState<HealthCheck | null>(null);
  const [loading, setLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const { toast } = useToast();

  const runHealthCheck = async () => {
    setLoading(true);
    try {
      // First check if user is admin
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('Authentication required');
      }

      // Verify admin role
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', session.user.id)
        .single();

      if (!profile || profile.role !== 'admin') {
        throw new Error('Admin privileges required');
      }

      console.log('Starting health check as admin user:', session.user.id);
      
      const { data, error } = await supabase.functions.invoke('system-health');
      
      if (error) {
        console.error('Health check function error:', error);
        throw error;
      }
      
      console.log('Health check response:', data);
      setHealthData(data);
      
      if (data.status === 'error') {
        toast({
          title: "Health Check Issues Detected",
          description: `${data.alerts?.length || 0} issues found`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Health Check Complete",
          description: `System status: ${data.status.toUpperCase()}`,
        });
      }
    } catch (error: any) {
      console.error('Health check failed:', error);
      toast({
        title: "Health Check Failed",
        description: error.message || "Unable to run system health check",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const runBackup = async () => {
    setBackupLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('backup-data');
      
      if (error) throw error;
      
      // Download the backup file
      const backupBlob = new Blob([JSON.stringify(data.full_backup, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(backupBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ascendry-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Backup Completed",
        description: `Backed up ${data.backup_summary.total_records} records from ${data.backup_summary.tables_backed_up.length} tables`,
      });
    } catch (error) {
      console.error('Backup failed:', error);
      toast({
        title: "Backup Failed",
        description: "Unable to create system backup",
        variant: "destructive",
      });
    } finally {
      setBackupLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variant = status === 'healthy' ? 'default' : status === 'warning' ? 'secondary' : 'destructive';
    return <Badge variant={variant}>{status.toUpperCase()}</Badge>;
  };

  useEffect(() => {
    const checkAuthAndRunHealthCheck = async () => {
      try {
        // Check if user is authenticated admin before running automatic health check
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('user_id', session.user.id)
          .single();

        if (profile?.role === 'admin') {
          await runHealthCheck();
        }
      } catch (error) {
        console.log('Skipping automatic health check - not admin or auth error');
      }
    };

    checkAuthAndRunHealthCheck();
    
    // Run health check every 5 minutes for authenticated admins only
    const interval = setInterval(checkAuthAndRunHealthCheck, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">System Health Monitor</h3>
          <p className="text-sm text-muted-foreground">
            Real-time monitoring of platform infrastructure
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={runHealthCheck}
            disabled={loading}
            variant="outline"
            size="sm"
          >
            <Activity className="h-4 w-4 mr-2" />
            {loading ? 'Checking...' : 'Run Check'}
          </Button>
          <Button
            onClick={runBackup}
            disabled={backupLoading}
            variant="outline"
            size="sm"
          >
            <Download className="h-4 w-4 mr-2" />
            {backupLoading ? 'Creating...' : 'Backup Data'}
          </Button>
        </div>
      </div>

      {healthData && (
        <>
          {/* Overall Status */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Overall System Status</CardTitle>
                {getStatusBadge(healthData.status)}
              </div>
              <CardDescription>
                Last checked: {new Date(healthData.timestamp).toLocaleString()}
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Alerts */}
          {healthData.alerts && healthData.alerts.length > 0 && (
            <div className="space-y-2">
              {healthData.alerts.map((alert, index) => (
                <Alert key={index} variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{alert}</AlertDescription>
                </Alert>
              ))}
            </div>
          )}

          {/* System Checks */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Database Check */}
            {healthData.checks.database && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Database</CardTitle>
                  <Database className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(healthData.checks.database.status)}
                    {getStatusBadge(healthData.checks.database.status)}
                  </div>
                  {healthData.checks.database.response_time_ms && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Response: {healthData.checks.database.response_time_ms}ms
                    </p>
                  )}
                  {healthData.checks.database.error && (
                    <p className="text-xs text-red-500 mt-2">
                      {healthData.checks.database.error}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Auth Check */}
            {healthData.checks.auth && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Authentication</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(healthData.checks.auth.status)}
                    {getStatusBadge(healthData.checks.auth.status)}
                  </div>
                  {healthData.checks.auth.error && (
                    <p className="text-xs text-red-500 mt-2">
                      {healthData.checks.auth.error}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Storage Check */}
            {healthData.checks.storage && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Storage</CardTitle>
                  <Cloud className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(healthData.checks.storage.status)}
                    {getStatusBadge(healthData.checks.storage.status)}
                  </div>
                  {healthData.checks.storage.buckets_count !== undefined && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Buckets: {healthData.checks.storage.buckets_count}
                    </p>
                  )}
                  {healthData.checks.storage.error && (
                    <p className="text-xs text-red-500 mt-2">
                      {healthData.checks.storage.error}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Performance Check */}
            {healthData.checks.performance && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Performance</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(healthData.checks.performance.status)}
                    {getStatusBadge(healthData.checks.performance.status)}
                  </div>
                  {healthData.checks.performance.query_time_ms && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Query: {healthData.checks.performance.query_time_ms}ms
                    </p>
                  )}
                  {healthData.checks.performance.error && (
                    <p className="text-xs text-red-500 mt-2">
                      {healthData.checks.performance.error}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Metrics */}
            {healthData.checks.metrics && (
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Platform Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Total Users</p>
                      <p className="font-medium">{healthData.checks.metrics.total_users}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Active Sessions</p>
                      <p className="font-medium">{healthData.checks.metrics.active_sessions}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Recent Errors</p>
                      <p className="font-medium">{healthData.checks.metrics.recent_errors}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
};